import { useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";

import { useUpdateFieldStatuses } from "@/hooks/mutations/use-assessments";
import { RootState } from "@/store";

import {
  AssessmentColumnNameEnum,
  AssessmentResultStatusEnum,
} from "@api/generated/types.gen";

import { ASSESSMENT_SECTIONS } from "../constants/sections";

type UseAutoExportProps = {
  assessmentData: any;
  assessmentId?: string;
  caseNumber?: string;
  checkInterval?: number; // in milliseconds, default 3000 (3 seconds)
  enabled?: boolean; // Whether auto-export is enabled
};

export const useAutoExport = ({
  assessmentData,
  assessmentId,
  caseNumber,
  checkInterval = 3000,
  enabled = true,
}: UseAutoExportProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [lastExportTime, setLastExportTime] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef<boolean>(false);
  const assessmentDataRef = useRef(assessmentData);
  const updateFieldStatuses = useUpdateFieldStatuses();

  // Keep ref in sync with latest assessmentData
  useEffect(() => {
    assessmentDataRef.current = assessmentData;
  }, [assessmentData]);

  // Check if there are active transcription tasks for this case
  const activeTranscriptionTasks = useSelector(
    (state: RootState) => state.transcription?.activeTasks || {}
  );

  const hasActiveTranscriptionTask = () => {
    if (!caseNumber || Object.keys(activeTranscriptionTasks).length === 0) {
      return false;
    }
    // Check if any active task matches the current case number
    return Object.values(activeTranscriptionTasks).some(
      (task: any) => task.caseNumber === caseNumber
    );
  };

  // Get fields that need to be exported (not EXPORTED status)
  const getUnprocessedFields = () => {
    const currentAssessmentData = assessmentDataRef.current;
    if (!currentAssessmentData) return [];

    const unprocessedFields: {
      column_name: AssessmentColumnNameEnum;
      status: AssessmentResultStatusEnum;
    }[] = [];
    const seenKeys = new Set<string>();

    ASSESSMENT_SECTIONS.forEach((section) => {
      if (
        section.key &&
        !seenKeys.has(section.key) &&
        currentAssessmentData[section.key]
      ) {
        seenKeys.add(section.key);
        const field = currentAssessmentData[section.key];
        // Check if the field has status information and is not EXPORTED
        if (field && typeof field === "object" && "status" in field) {
          if (field.status !== "EXPORTED") {
            unprocessedFields.push({
              column_name: section.key,
              status: "EXPORTED",
            });
          }
        }
      }
    });

    return unprocessedFields;
  };

  // Check if there are unprocessed fields
  const hasUnprocessedFields = () => {
    return getUnprocessedFields().length > 0;
  };

  // Auto-export unprocessed fields
  const autoExportFields = async () => {
    // Prevent multiple simultaneous calls
    if (isRunningRef.current) {
      return;
    }

    if (!assessmentId || !caseNumber || isExporting) {
      return;
    }

    // Don't auto-export if there's an active transcription task for this case
    if (hasActiveTranscriptionTask()) {
      return;
    }

    const unprocessedFields = getUnprocessedFields();
    if (unprocessedFields.length === 0) {
      return;
    }

    // Throttle exports - don't export more than once every 5 seconds
    const now = Date.now();
    if (now - lastExportTime < 5000) {
      return;
    }

    try {
      isRunningRef.current = true;
      setIsExporting(true);
      setLastExportTime(now);

      await updateFieldStatuses.mutateAsync({
        assessmentId,
        caseNumber,
        fieldStatuses: unprocessedFields,
      });

      // Optimistically update the ref to mark these fields as EXPORTED
      // This prevents the interval from finding the same fields again before the refetch completes
      if (assessmentDataRef.current) {
        const updatedData = { ...assessmentDataRef.current };
        unprocessedFields.forEach((field) => {
          const fieldKey = field.column_name;
          if (updatedData[fieldKey] && typeof updatedData[fieldKey] === "object") {
            updatedData[fieldKey] = {
              ...updatedData[fieldKey],
              status: "EXPORTED",
            };
          }
        });
        assessmentDataRef.current = updatedData;
      }

      console.log(`Auto-exported ${unprocessedFields.length} assessment fields`);
    } catch (error) {
      console.error("Error auto-exporting assessment fields:", error);
    } finally {
      setIsExporting(false);
      isRunningRef.current = false;
    }
  };

  // Start auto-export monitoring
  const startAutoExport = () => {
    // Don't start if there's already an active transcription task
    if (hasActiveTranscriptionTask()) {
      stopAutoExport();
      return;
    }

    // Don't restart if interval is already running
    if (intervalRef.current) {
      return;
    }

    intervalRef.current = setInterval(() => {
      // Stop the interval if a transcription task becomes active
      if (hasActiveTranscriptionTask()) {
        stopAutoExport();
        return;
      }

      // Don't call autoExportFields if it's already running
      if (isRunningRef.current || isExporting) {
        return;
      }

      if (enabled && hasUnprocessedFields() && !hasActiveTranscriptionTask()) {
        autoExportFields();
      }
    }, checkInterval);
  };

  // Stop auto-export monitoring
  const stopAutoExport = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Manual trigger for immediate export
  const triggerAutoExport = () => {
    if (enabled && !hasActiveTranscriptionTask()) {
      autoExportFields();
    }
  };

  // Start monitoring when component mounts or dependencies change
  useEffect(() => {
    // Don't start auto-export if there's an active transcription task
    if (hasActiveTranscriptionTask()) {
      stopAutoExport();
      return;
    }

    // Only start if we have the required data and interval is not already running
    if (enabled && assessmentId && caseNumber && assessmentData) {
      // Only start if interval is not already running
      if (!intervalRef.current) {
        startAutoExport();
      }
    } else {
      stopAutoExport();
    }

    return () => {
      stopAutoExport();
    };
  }, [
    enabled,
    assessmentId,
    caseNumber,
    // Note: assessmentData is intentionally not in dependencies to avoid restarting
    // the interval on every refetch. The interval checks for unprocessed fields on each tick.
    checkInterval,
    activeTranscriptionTasks,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoExport();
    };
  }, []);

  return {
    hasUnprocessedFields: hasUnprocessedFields(),
    isExporting,
    triggerAutoExport,
    startAutoExport,
    stopAutoExport,
    unprocessedFieldsCount: getUnprocessedFields().length,
  };
};
